/* eslint-disable @typescript-eslint/no-explicit-any */

import { SortOrder } from './job_candidate.service';
import { Candidate } from '@/types/Candidate';
import { isAxiosError } from 'axios';
import { ApiResponse, api } from '../api';
import { CANDIDATE_URLS } from '../url';
import { JobBasicInfo, JobWithMatchInfo } from '@/types/Job';

export type GetCandidatesSortBy = 'created_at' | 'name' | 'unique_id';

export interface CreateCandidatePayload {
  resume: File;
}

export const apiCreateCandidate = async ({
  payload
}: {
  payload: CreateCandidatePayload;
}): Promise<ApiResponse<Candidate>> => {
  try {
    const formData = new FormData();
    formData.append('resume', payload.resume);

    const response = await api.post<ApiResponse<Candidate>>(CANDIDATE_URLS.CREATE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface CreateCandidateFromVeteransResumePayload {
  file: File;
}

export const apiCreateCandidateFromVeteransResume = async ({
  payload
}: {
  payload: CreateCandidateFromVeteransResumePayload;
}): Promise<ApiResponse<Candidate>> => {
  try {
    const formData = new FormData();
    formData.append('file', payload.file);

    const response = await api.post<ApiResponse<Candidate>>(
      CANDIDATE_URLS.CREATE_FROM_VETERANS_RESUME,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetCandidatesParams {
  page: number;
  limit: number;
  sort_by: GetCandidatesSortBy;
  sort_order: SortOrder;
  keyword: string;
  fit_positions?: string[];
}

export interface GetCandidatesResponse {
  candidates: Candidate[];
  current_page: number;
  total_pages: number;
  total_count: number;
}

export const apiGetCandidates = async ({
  params
}: {
  params: GetCandidatesParams;
}): Promise<ApiResponse<GetCandidatesResponse>> => {
  try {
    // Custom params serializer to handle arrays without [] notation
    const paramsSerializer = (params: any) => {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // For arrays, add each value with the same key (no [] notation)
          value.forEach((item) => {
            searchParams.append(key, String(item));
          });
        } else if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });

      return searchParams.toString();
    };

    const response = await api.get<ApiResponse<GetCandidatesResponse>>(CANDIDATE_URLS.GET_ALL, {
      params,
      paramsSerializer
    });

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiGetVeteransResumeCandidates = async ({
  params
}: {
  params: GetCandidatesParams;
}): Promise<ApiResponse<GetCandidatesResponse>> => {
  try {
    // Custom params serializer to handle arrays without [] notation
    const paramsSerializer = (params: any) => {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // For arrays, add each value with the same key (no [] notation)
          value.forEach((item) => {
            searchParams.append(key, String(item));
          });
        } else if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });

      return searchParams.toString();
    };

    const response = await api.get<ApiResponse<GetCandidatesResponse>>(
      CANDIDATE_URLS.GET_FROM_VETERANS_RESUME,
      {
        params,
        paramsSerializer
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiGetCandidateProfile = async ({
  candidateId
}: {
  candidateId: string;
}): Promise<ApiResponse<Candidate>> => {
  try {
    const response = await api.get<ApiResponse<Candidate>>(CANDIDATE_URLS.GET_PROFILE(candidateId));

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiGetCandidateMatchJobs = async ({
  candidateId
}: {
  candidateId: string;
}): Promise<ApiResponse<JobWithMatchInfo[]>> => {
  try {
    const response = await api.get<ApiResponse<JobWithMatchInfo[]>>(
      CANDIDATE_URLS.GET_MATCH_JOBS(candidateId)
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface UpdateCandidateProfilePayload {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  linkedin: string;
}

export const apiUpdateCandidateProfile = async ({
  candidateId,
  payload
}: {
  candidateId: string;
  payload: UpdateCandidateProfilePayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.put<ApiResponse<void>>(
      CANDIDATE_URLS.UPDATE_PROFILE(candidateId),
      payload
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiDeleteCandidate = async ({
  candidateId
}: {
  candidateId: string;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.delete<ApiResponse<void>>(CANDIDATE_URLS.DELETE(candidateId));

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetFitPositionsResponse {
  fit_positions: string[];
}

export const apiGetFitPositions = async (): Promise<ApiResponse<GetFitPositionsResponse>> => {
  try {
    const response = await api.get<ApiResponse<GetFitPositionsResponse>>(
      CANDIDATE_URLS.GET_FIT_POSITIONS
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetCandidateAvailableJobsParams {
  page: number;
  limit: number;
  keyword: string;
}

export interface GetCandidateAvailableJobsResponse {
  jobs: JobBasicInfo[];
  current_page: number;
  total_pages: number;
  total_count: number;
  limit: number;
}

export const apiGetCandidateAvailableJobs = async ({
  candidateId,
  params
}: {
  candidateId: string;
  params: GetCandidateAvailableJobsParams;
}): Promise<ApiResponse<GetCandidateAvailableJobsResponse>> => {
  try {
    // Custom params serializer to handle arrays without [] notation
    const paramsSerializer = (params: any) => {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // For arrays, add each value with the same key (no [] notation)
          value.forEach((item) => {
            searchParams.append(key, String(item));
          });
        } else if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });

      return searchParams.toString();
    };

    const response = await api.get<ApiResponse<GetCandidateAvailableJobsResponse>>(
      CANDIDATE_URLS.GET_AVAILABLE_JOBS(candidateId),
      {
        params,
        paramsSerializer
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};
